/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import moment from "moment";
import React from "react";
import {applePay, dataCollector, googlePayment, paypalCheckout} from "braintree-web";
import paypal from "paypal-checkout";
import {convertRegionToIsocode} from "../data";
import {text} from './i18n';

export const initialPayment = {
    // TODO: match these up with the real data
    method: '',
    cardType: '',
    expiration: ''
};

// NOTE: each method property value must match what is in
// doterra.payment.digitalWallets.enabled for it to be enabled
export const paymentMethods = [
    {method: 'card', name: 'Debit or Credit Card', icon: 'payment-generic'},
    {method: 'paypal', name: 'Paypal', icon: 'payment-paypal'},
    {method: 'apple-pay', name: 'Apple Pay', icon: 'payment-apple'},
    {method: 'google-pay', name: 'Google Pay', icon: 'payment-google_pay'}
];

const paymentTypes = [
    {type: 'visa', name: 'Visa'},
    {type: 'mastercard', name: 'MasterCard'},
    {type: 'amex', name: 'American Express'},
    {type: 'discover', name: 'Discover'},
    {type: 'paypal', name: 'PayPal'},
    {type: 'apple', name: 'Apple Pay'},
    {type: 'google_pay', name: 'Google Pay'}
];
export const monthNumbers = ['01','02','03','04','05','06','07','08','09','10','11','12'];

// override mapping from hybris
const hybrisCardTypeOverridesMap = {
    'master': 'mastercard',
    'apple_pay':'apple'
};

export const formatCCExpiry = (month, year) => {
    return `${month}/${year}`;
};

export const getCardTypeFromHybrisType = (hybrisType) => {
    const mappedType = hybrisCardTypeOverridesMap[hybrisType] || hybrisType;
    const filtered = paymentTypes.filter(cardType => cardType.type === mappedType);
    return filtered.length > 0 ? filtered[0].type : undefined;
};

const getPaymentType = (hybrisType) => {
    if (!hybrisType) {
        return undefined;
    }
    let type = hybrisType.toLowerCase();
    type = hybrisCardTypeOverridesMap[type] || type;
    return paymentTypes.find(t => t.type === type);
};

export const getIconNameForPaymentType = (hybrisType) => {
    const paymentType = getPaymentType(hybrisType);
    return paymentType ? `payment-${paymentType.type}` : undefined;
};

export const getNameForPaymentType = (hybrisType) => {
    const paymentType = getPaymentType(hybrisType);
    return paymentType ? `${paymentType.name}`: undefined;

};

let expiryYears = [];
for (let i = moment().year(); i < moment().year() + 16; i++) {
    expiryYears.push(i.toString());
}

const selectData = (data) => {
  return data.map((datum, index) => <option key={index} value={datum}>{datum}</option>);
};
export const monthOptionData = selectData(monthNumbers);
export const yearOptionData = selectData(expiryYears);

const doterraSelectData = (data) => {
  return data.map((datum, index) => {
    return {label: datum, value: datum};
  });
};
export const doterraSelectMonthOptionData = doterraSelectData(monthNumbers);
export const doterraSelectYearOptionData = doterraSelectData(expiryYears);


export const getCCInfo = (payment) => {
    if (!payment) {
        throw new Error('getCCInfo: Invalid payment: payment is null.');
    }
    if (!payment.method || payment.method !== 'card') {
        throw new Error('getCCInfo: Invalid payment type: payment is not a card.');
    }
    return {
        number: payment.number,
        displayNumber: payment.number.substring(12, 16),
        expiry: formatCCExpiry(payment.expiryMonth,payment.expiryYear),
        holderName: payment.holderName,
        status: payment.status
    };
};

export const getUnique = (arr, comp) => {
    return arr
        .map(e => e[comp])
        // store the keys of the unique objects
        .map((e, i, final) => final.indexOf(e) === i && i)
        // eliminate the dead keys & store unique objects
        .filter(e => arr[e]).map(e => arr[e]);
};

export const initiatePayPal = (altPaymentParameters, containerId, setSubscriptionPayment, subscriptionCode) => {
    paypalCheckout.create({
        client: altPaymentParameters.clientInstance
    }, (paypalCheckoutErr, paypalCheckoutInstance) => {

        // Stop if there was a problem creating PayPal Checkout.
        // This could happen if there was a network error or if it's incorrectly
        // configured.
        if (paypalCheckoutErr) {
            console.error('Error creating PayPal Checkout:', paypalCheckoutErr);
            return;
        }
        let deviceData = {};

        dataCollector.create({
            client: altPaymentParameters.clientInstance,
            paypal: true
        }, (err, dataCollectorInstance) => {
            if (err) {
                console.error("DCerr",err);
                // Handle error in creation of data collector
                return;
            }
            // At this point, you should access the dataCollectorInstance.deviceData value and provide it
            // to your server, e.g. by injecting it into your form as a hidden input.
            deviceData = dataCollectorInstance.deviceData;

        });

        // Set up PayPal with the checkout.js library
        paypal.Button.render({
            env: altPaymentParameters.digitalWalletEnv, // or 'production'
            style: {
                size: 'responsive',
                color: 'blue',
                shape: 'rect',
                label: 'paypal',
                tagline: 'false',
                height: 40
            },
            payment: () => {
                return paypalCheckoutInstance.createPayment({
                    flow: 'vault', // Required
                    // amount: 10.00, // Required
                    // currency: 'USD', // Required
                    locale: '',

                    enableShippingAddress:true
                });
            },

            onAuthorize: data => {
                return paypalCheckoutInstance.tokenizePayment(data, (err, payload) => {
                    // deviceData is needed when we do a transaction from a vaulted Paypal wallet
                    // it reduces the declines from paypal when included
                    const digitalWalletPayload = {
                        nonce: payload.nonce,
                        digitalWalletType: payload.type,
                        email: payload.details.email,
                        displayedAcct: payload.details.email,
                        fName: payload.details.firstName,
                        lName: payload.details.lastName,
                        payerId: payload.details.payerId,
                        phone: payload.details.phone,
                        nameOnCard: `${payload.details.firstName} ${payload.details.lastName}`,
                        billingLine1: payload.details.billingAddress.line1,
                        billingLine2: payload.details.billingAddress.line2,
                        billingCity: payload.details.billingAddress.city,
                        billingState: payload.details.billingAddress.state,
                        billingZip: payload.details.billingAddress.postalCode,
                        market: payload.details.billingAddress.countryCode,
                        shippingLine1: payload.details.shippingAddress.line1,
                        shippingLine2: payload.details.shippingAddress.line2,
                        shippingCity: payload.details.shippingAddress.city,
                        shippingState: payload.details.shippingAddress.state,
                        shippingZip: payload.details.shippingAddress.postalCode,
                        shippingCountryCode: payload.details.shippingAddress.countryCode,
                        shippingAddress: payload.details.shippingAddress,
                        deviceData,
                        makeDefault: document.getElementById("makeDefault") ? document.getElementById("makeDefault").checked : true
                    };
                    altPaymentParameters.addPayment(digitalWalletPayload, subscriptionCode);
                    altPaymentParameters.showPaymentList();
                    if (setSubscriptionPayment) {
                      const paymentData = {
                        method: 'digital-wallet',
                        code: digitalWalletPayload.displayedAcct,
                        type: 'PAYPAL'
                      };

                      setSubscriptionPayment(paymentData);
                    }
                    if (altPaymentParameters.modelName) {
                        altPaymentParameters.hideModal(altPaymentParameters.modelName);
                    }
                });
            },

            onCancel: data => {
                console.log('checkout.js payment cancelled', JSON.stringify(data, 0, 2));
            },

            onError: err => {
                console.error("braintree error1", err);
            },

            onRender: () => {
                const child = document.getElementById(containerId).getElementsByClassName('spinner')[0];
                child.remove();
            }
        }, '#'+ containerId);
    });
};


export const initiateGooglePay = (altPaymentParameters, containerId, setSubscriptionPayment, subscriptionCode) => {

    let envParameter = altPaymentParameters.digitalWalletEnv;
    if (altPaymentParameters.digitalWalletEnv === 'sandbox' || altPaymentParameters.digitalWalletEnv === 'SANDBOX') {
        envParameter = 'TEST';
    } else if (envParameter !== null) {
        envParameter = envParameter.toUpperCase();
    }


    const paymentsClient = new google.payments.api.PaymentsClient({
        environment: envParameter // Or 'PRODUCTION'
    });
    googlePayment.create({
        client: altPaymentParameters.clientInstance,
        googlePayVersion: 2,
        googleMerchantId: altPaymentParameters.googlePayMerchantId
    }, (googlePaymentErr, googlePaymentInstance) => {
        paymentsClient.isReadyToPay({
            // see https://developers.google.com/pay/api/web/reference/object#IsReadyToPayRequest
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest().allowedPaymentMethods,
            existingPaymentMethodRequired: true // Optional
        }).then( (response) => {
            if (response.result) {
                const googlePayButton = paymentsClient.createButton({
                    buttonType:'short',
                    buttonColor:'black',
                    onClick: (event) => {
                        event.preventDefault();

                        const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest(
                            {transactionInfo: {
                                    currencyCode: altPaymentParameters.currencyCode,
                                    totalPriceStatus: 'NOT_CURRENTLY_KNOWN'
                                },
                                emailRequired: true,
                                shippingAddressRequired: true,
                                shippingAddressParameters: {
                                    allowedCountryCodes: ['US','CA'],
                                    phoneNumberRequired: true
                                }
                            });
                        // We recommend collecting billing address information, at minimum
                        // billing postal code, and passing that billing postal code with all
                        // Google Pay card transactions as a best practice.
                        // See all available options at https://developers.google.com/pay/api/web/reference/object
                        const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
                        cardPaymentMethod.parameters.billingAddressRequired = true;
                        cardPaymentMethod.parameters.billingAddressParameters = {
                            format: 'FULL',
                            phoneNumberRequired: true

                        };

                        paymentsClient.loadPaymentData(paymentDataRequest).then(function (paymentData) {
                            googlePaymentInstance.parseResponse(paymentData, function (err, result) {
                                if (err) {
                                    // Handle parsing error
                                    console.log("Parsing error ");

                                }
                                let googlePayType = 'GOOGLE_PAY';

                                const digitalWalletPayload = {
                                    nonce: result.nonce,
                                    digitalWalletType: googlePayType,
                                    email: paymentData.email,
                                    displayedAcct: paymentData.paymentMethodData.description,
                                    nameOnCard: paymentData.paymentMethodData.info.billingAddress.name,
                                    market: paymentData.paymentMethodData.info.billingAddress.countryCode,
                                    billingLine1: paymentData.paymentMethodData.info.billingAddress.address1,
                                    billingLine2: paymentData.paymentMethodData.info.billingAddress.address2,
                                    billingCity: paymentData.paymentMethodData.info.billingAddress.locality,
                                    billingState: paymentData.paymentMethodData.info.billingAddress.administrativeArea,
                                    billingZip: paymentData.paymentMethodData.info.billingAddress.postalCode,
                                    shippingLine1: paymentData.shippingAddress.address1,
                                    shippingLine2: paymentData.shippingAddress.address2,
                                    shippingCity: paymentData.shippingAddress.locality,
                                    shippingState: paymentData.shippingAddress.administrativeArea,
                                    shippingCountryCode: paymentData.shippingAddress.countryCode,
                                    shippingZip: paymentData.shippingAddress.postalCode,
                                    shippingAddress: paymentData.shippingAddress,
                                    phone: paymentData.shippingAddress.phoneNumber,
                                    fName: paymentData.shippingAddress.name.includes(' ')?paymentData.shippingAddress.name.split(' ')[0]:paymentData.shippingAddress.name,
                                    lName: paymentData.shippingAddress.name.includes(' ')?paymentData.shippingAddress.name.split(' ')[1]:'',
                                    makeDefault: document.getElementById("makeDefault") ? document.getElementById("makeDefault").checked : true
                                };
                                altPaymentParameters.addPayment(digitalWalletPayload, subscriptionCode);
                                altPaymentParameters.showPaymentList();

                              if (setSubscriptionPayment) {
                                const paymentData = {
                                  method: 'digital-wallet',
                                  code: digitalWalletPayload.displayedAcct,
                                  type: 'GOOGLE_PAY'
                                };

                                setSubscriptionPayment(paymentData);
                              }

                                if (altPaymentParameters.modelName) {
                                    altPaymentParameters.hideModal(altPaymentParameters.modelName);
                                }
                                // Send result.nonce to your server
                                // result.type may be either "AndroidPayCard" or "PayPalAccount", and
                                // paymentData will contain the billingAddress for card payments
                            });

                        }).catch((err) => {
                            // Handle errors
                            console.log("Error with loadPaymentData",err);
                        });
                    }
                });

                const child = document.getElementById(containerId).getElementsByClassName('spinner')[0];
                if (child) {
                    child.parentNode.removeChild(child);
                    const parent = document.getElementById(containerId);
                    if (!parent.hasChildNodes())
                        {parent.appendChild(googlePayButton);}
                }
            }
        }).catch((err) => {
            // Handle errors
            console.log("Error with isReadyToPay",err);
        });
    });
};

export const initiateApplePay = (altPaymentParameters, containerId, setSubscriptionPayment, subscriptionCode) => {
    applePay.create({
        client: altPaymentParameters.clientInstance
    }, (applePayErr, applePayInstance) => {
        if (applePayErr) {
            console.log('Error creating applePayInstance:', applePayErr);
            return;
        }
        if (!applePaySupported()) {
            altPaymentParameters.handleAlternatePaymentError(null,text("doterra.applePay.browserNotSupported"));
            return;
        }
        const promise = ApplePaySession.canMakePaymentsWithActiveCard(applePayInstance.merchantIdentifier);
        promise.then(canMakePaymentsWithActiveCard => {
            // Set up Apple Pay buttons
      const applePayButton = document.createElement("button");
      applePayButton.id = "add-apple-pay-button-doterra";
      applePayButton.className = "apple-pay-button";
      applePayButton.style = "-apple-pay-button-type: plain;";
      applePayButton.onclick = handleApplePayButtonClick;
      window.applePayInfoMap = {applePayInstance:applePayInstance, altPaymentParameters:altPaymentParameters, subscriptionCode:subscriptionCode, setSubscriptionPayment:setSubscriptionPayment};
      document.getElementById(containerId).appendChild(applePayButton);
            const child = document.getElementById(containerId).getElementsByClassName('spinner')[0];
            child.remove();

    }).catch((err)=>{
      console.log("ERROR OCCURED: ",err);
    });

  });
};

function handleApplePayButtonClick() {

  let paymentRequest = window.applePayInfoMap.applePayInstance.createPaymentRequest({
                                                                                      "currencyCode": window.applePayInfoMap.altPaymentParameters.currencyCode,
                                                                                      "merchantCapabilities": [
                                                                                        "supports3DS"
                                                                                      ],
                                                                                      "supportedNetworks": [
                                                                                        "visa",
                                                                                        "masterCard",
                                                                                        "amex",
                                                                                        "discover"
                                                                                      ],
                                                                                      "total": {
                                                                                        "label": text("doterra.applePay.vaultingApplePay"),
                                                                                        "type": "pending",
                                                                                        "amount": "0.00"
                                                                                      },
                                                                                      requiredShippingContactFields: ['postalAddress', 'phone', 'email', 'name'],
                                                                                      requiredBillingAddressFields: ['phone', 'email', 'name', 'postalAddress'],
                                                                                      requiredBillingContactFields: ['postalAddress', 'phone', 'email', 'name']

                                                                                    });
  let session = new ApplePaySession(6, paymentRequest);

  session.onvalidatemerchant = (event) => {
    window.applePayInfoMap.applePayInstance.performValidation({
                                                                validationURL: event.validationURL,
                                                                displayName: 'Doterra'
                                                              }, (err, merchantSession) => {
      if(err) {
        // You should show an error to the user, e.g. 'Apple Pay failed to load.'
        console.log("Apple Pay failed to load");
        return;
      }
      session.completeMerchantValidation(merchantSession);
    });
  };

  session.onpaymentauthorized = (event) => {

    window.applePayInfoMap.applePayInstance.tokenize({
                                                       token: event.payment.token
                                                     }, (tokenizeErr, payload) => {
      if(tokenizeErr) {
        console.error('Error tokenizing Apple Pay:', tokenizeErr);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        return;
      }

      const applePayType = 'APPLE_PAY';
      const billingContactEvent = event.payment.billingContact;
      const shippingContactEvent = event.payment.shippingContact;
      const region = convertRegionToIsocode(billingContactEvent.countryCode, billingContactEvent.administrativeArea);
      const shippingRegion = convertRegionToIsocode(shippingContactEvent.countryCode, shippingContactEvent.administrativeArea);
      if(!region) {
        window.applePayInfoMap.altPaymentParameters.handleAlternatePaymentError(null, text("doterra.applePay.invalidRegion"));
        console.error('Error with region', region);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        return;
      }
      const nameOnCard = payload.details.cardholderName ?
        payload.details.cardholderName :
        `${billingContactEvent.givenName ? billingContactEvent.givenName : ''}  ${billingContactEvent.familyName ? billingContactEvent.familyName : ''}`
          .trim();
      const digitalWalletPayload = {
        nonce: payload.nonce,
        digitalWalletType: applePayType,
        email: shippingContactEvent.emailAddress,
        displayedAcct: payload.details.paymentInstrumentName,
        nameOnCard: `${billingContactEvent.givenName} ${billingContactEvent.familyName}`,
        market: billingContactEvent.countryCode.toUpperCase(),
        billingLine1: billingContactEvent.addressLines[0],
        billingLine2: billingContactEvent.addressLines[1],
        billingCity: billingContactEvent.locality,
        billingState: region,
        billingZip: billingContactEvent.postalCode,
        shippingLine1: shippingContactEvent.addressLines[0],
        shippingLine2: shippingContactEvent.addressLines[1],
        shippingCity: shippingContactEvent.locality,
        shippingState: shippingRegion,
        shippingCountryCode: shippingContactEvent.countryCode,
        shippingZip: shippingContactEvent.postalCode,
        phone: shippingContactEvent.phoneNumber,
        fName: shippingContactEvent.givenName,
        lName: shippingContactEvent.familyName,
        makeDefault: document.getElementById("makeDefault") ? document.getElementById("makeDefault").checked : true
      };
      window.applePayInfoMap.altPaymentParameters.addPayment(digitalWalletPayload, window.applePayInfoMap.subscriptionCode);
      // Send payload.nonce to your server.
      // After you have transacted with the payload.nonce,
      // call `completePayment` to dismiss the Apple Pay sheet.
      session.completePayment(ApplePaySession.STATUS_SUCCESS);
      window.applePayInfoMap.altPaymentParameters.showPaymentList();

      if(window.applePayInfoMap.setSubscriptionPayment) {
        const paymentData = {
          method: 'digital-wallet',
          code: digitalWalletPayload.displayedAcct,
          type: 'APPLE_PAY'
        };

        window.applePayInfoMap.setSubscriptionPayment(paymentData);
      }

      if(window.applePayInfoMap.altPaymentParameters.modelName) {
        window.applePayInfoMap.altPaymentParameters.hideModal(window.applePayInfoMap.altPaymentParameters.modelName);
      }
    });
  };
  session.begin();
}
/*****************************************/
/** Check whether APPLE PAY supported   **/
/*****************************************/
export const applePaySupported = () => {
    let applePaySupported;

    //If the call comes from mobile dev ios, make the applePaySupported to be true.
    if (navigator && navigator.userAgent && navigator.userAgent.includes("FABuild-ios")) {
        applePaySupported = true;
    } else {
        applePaySupported = window.ApplePaySession && window.ApplePaySession.canMakePayments() && window.ApplePaySession.supportsVersion(6);
    }

    return applePaySupported;
};


